import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getUserSettings, updateUserSetting } from "services";

import { getLocalKlimVar, useUserContext } from "utils";

import { FIELD_VIEW_MODE_ALERT_DISMISSALS, GET_USER_SETTINGS, UPDATE_USER_SETTING } from "consts";

import { UserSettings } from "models";

/** The sibling dismissal settings hold a count as a string, so a single dismissal is "1". */
const DISMISSED_ONCE = "1";
const USER_SETTINGS_STALE_TIME_MS = 1000 * 60 * 5;

/**
 * Tracks whether the table and map views have already been announced to this user.
 *
 * The flag lives on the user rather than on the farm: a farmer with several farms should meet the
 * announcement once in total, not once per farm they switch into.
 */
export const useFieldViewModeAnnouncementSeen = () => {
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  const token = getLocalKlimVar("token");
  const isAuthorized = !!user && !!token;

  const userSettingsQuery = useQuery<UserSettings>({
    queryKey: [GET_USER_SETTINGS],
    queryFn: ({ signal }) => getUserSettings({ signal }),
    staleTime: USER_SETTINGS_STALE_TIME_MS,
    enabled: isAuthorized,
  });

  const { mutate: markAnnouncementSeen } = useMutation({
    mutationKey: [UPDATE_USER_SETTING, FIELD_VIEW_MODE_ALERT_DISMISSALS],
    mutationFn: () => {
      if (!isAuthorized) return Promise.reject(new Error("Not Authorized"));
      return updateUserSetting({ item: FIELD_VIEW_MODE_ALERT_DISMISSALS, value: DISMISSED_ONCE });
    },
    onSuccess: () => {
      // written straight into the cache so a farm switch in the same session cannot re-announce
      queryClient.setQueryData<UserSettings>([GET_USER_SETTINGS], (previousSettings) =>
        previousSettings ? { ...previousSettings, [FIELD_VIEW_MODE_ALERT_DISMISSALS]: DISMISSED_ONCE } : previousSettings
      );
      queryClient.invalidateQueries({ queryKey: [GET_USER_SETTINGS] });
    },
  });

  const dismissalCount = Number(userSettingsQuery.data?.[FIELD_VIEW_MODE_ALERT_DISMISSALS] ?? 0);

  return {
    hasSeenAnnouncement: dismissalCount > 0,
    /** The announcement has to stay hidden until this resolves, or it flashes up for users who already saw it. */
    isLoadingAnnouncementSeen: isAuthorized && userSettingsQuery.isLoading,
    markAnnouncementSeen,
  };
};
